'use client'

import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

const POLL_MS = 45_000
const AUTO_RELOAD_MS = 10_000

async function fetchDeployVersion(): Promise<string | null> {
  try {
    const res = await fetch('/api/version', { cache: 'no-store' })
    if (!res.ok) return null
    const data = (await res.json()) as { version?: string }
    return typeof data.version === 'string' && data.version ? data.version : null
  } catch {
    return null
  }
}

/**
 * Polls the web deploy version and reloads open tabs when a new build is live.
 * Frontend-only — does not touch mobile version controllers.
 */
const WebVersionRefresh = () => {
  const baselineRef = useRef<string | null>(null)
  const reloadingRef = useRef(false)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return

    const triggerReload = (nextVersion: string) => {
      if (reloadingRef.current) return
      reloadingRef.current = true

      toast.info(
        ({ closeToast }) => (
          <div className='flex flex-col gap-2'>
            <span>A new web update is available.</span>
            <button
              type='button'
              className='text-left font-medium underline'
              onClick={() => {
                closeToast?.()
                globalThis.location.reload()
              }}
            >
              Refresh now
            </button>
          </div>
        ),
        { autoClose: false, toastId: 'web-version-refresh' }
      )

      globalThis.setTimeout(() => {
        globalThis.location.reload()
      }, AUTO_RELOAD_MS)

      // Keep baseline in sync if reload is blocked somehow
      baselineRef.current = nextVersion
    }

    const check = async () => {
      const version = await fetchDeployVersion()
      if (!version) return

      if (baselineRef.current === null) {
        baselineRef.current = version
        return
      }

      if (version !== baselineRef.current) {
        triggerReload(version)
      }
    }

    void check()
    const intervalId = globalThis.setInterval(() => void check(), POLL_MS)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    globalThis.addEventListener('focus', onVisible)

    return () => {
      globalThis.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      globalThis.removeEventListener('focus', onVisible)
    }
  }, [])

  return null
}

export default WebVersionRefresh
